import { MINUTES_PER_DAY } from "@/lib/clock";

/** Where "late" stops and "early" begins, measured from the target. */
const LATE_WINDOW_MINUTES = MINUTES_PER_DAY / 2;

/**
 * Minutes from `target` forward to `logged`, wrapping past midnight. Sleep
 * times carry no date (docs/adr/0008), so this can never go negative: 23:30
 * against a 00:00 target comes out as 1410, not as −30. Deciding whether
 * that means "nearly a day late" or "half an hour early" is `minutesLate`'s
 * job, not this one's.
 */
function minutesForward(target: number, logged: number): number {
  return (
    (((logged - target) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
  );
}

/**
 * How late `logged` was against `target`, or null when it reads as early.
 * Times within 12 hours after the target are late by exactly that much;
 * anything further round the clock is taken as early and scores full points
 * (CONTEXT.md: "Late window").
 */
export function minutesLate(target: number, logged: number): number | null {
  const elapsed = minutesForward(target, logged);
  return elapsed > LATE_WINDOW_MINUTES ? null : elapsed;
}

/**
 * Minutes slept. Wraps midnight the same way, which is the normal case —
 * falling asleep at 23:30 and waking at 06:45 is 7h15m, not −16h45m.
 */
export function sleepDurationMinutes(asleep: number, awake: number): number {
  return minutesForward(asleep, awake);
}
